import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertType,
  ButtonGroup,
  Dialog,
  Divider,
  ProspectDetails,
  TagsAndNotesCell,
  TextButton,
  Typography,
  TypographyType,
} from '../../../components';
import { ACTIVE_TAB_PARAM, AppRoutes } from '../../../constants';
import { useAppDispatch, useAppSelector, useHandleApiResponse, useToast } from '../../../hooks';
import { setPersonaPrompt, setProspectForm, updateFooterFields } from '../../../redux/reducers';
import { useRegeneratePromptMutation } from '../../../services';
import {
  Orientation,
  Permissions,
  ProspectPageMode,
  ProspectPageTabs,
  SimulationsTab,
  UpdatedPracticeProspectData,
} from '../../../types';
import CreateEndActions from './CreateEndActions';
import EditEndActions from './EditEndActions';

const PROSPECT_FOOTER_HEIGHT = 88;
const REGENERATE_PROMPT_ERROR_MSG = 'Failed to regenerate prompt.';

interface ProspectPageFooterProps {
  activeTab: ProspectPageTabs;
  visitedTabs: Set<ProspectPageTabs>;
  // Resets the form to the original prospect data, discarding any unsaved changes.
  resetForm: () => void;
  setActiveTab: Dispatch<SetStateAction<ProspectPageTabs>>;
  setVisitedTabs: Dispatch<SetStateAction<Set<ProspectPageTabs>>>;
}

const ProspectPageFooter = ({
  activeTab,
  visitedTabs,
  resetForm,
  setActiveTab,
  setVisitedTabs,
}: ProspectPageFooterProps) => {
  const navigate = useNavigate();

  // State for the regenerate prompt dialog that appears after saving changes.
  // - open: Controls dialog visibility.
  // - isDraft: Determines navigation behavior when saving without regenerating.
  const [regeneratePromptDialog, setRegeneratePromptDialog] = useState<{ open: boolean; isDraft: boolean }>({
    open: false,
    isDraft: false,
  });

  const dispatch = useAppDispatch();
  const {
    personaId,
    mode,
    tags,
    notes,
    isDraft,
    isFavorited,
    fields: { firstName, lastName, accountName, jobTitle, isSubscribed, allowedCallers, allowedTeams },
  } = useAppSelector((state) => state.prospect);
  const user = useAppSelector((state) => state.auth.user);
  const isSuperAdmin = (user?.permissions || []).includes(Permissions.ADMIN_ACCESS);

  const handleApiResponse = useHandleApiResponse();
  const { showToast } = useToast();

  const [regeneratePrompt, { isLoading: isRegeneratingPrompt }] = useRegeneratePromptMutation();

  const activeTabIndex = Object.values(ProspectPageTabs).indexOf(activeTab);

  const showProspectDetails =
    mode !== ProspectPageMode.CREATE ||
    activeTabIndex > Object.values(ProspectPageTabs).indexOf(ProspectPageTabs.PERSONAL);

  const showJobTitle =
    mode !== ProspectPageMode.CREATE || activeTabIndex > Object.values(ProspectPageTabs).indexOf(ProspectPageTabs.LEAD);

  const showAccountName =
    mode !== ProspectPageMode.CREATE ||
    activeTabIndex > Object.values(ProspectPageTabs).indexOf(ProspectPageTabs.ACCOUNT);

  const showTagsAndNotes =
    mode !== ProspectPageMode.CREATE ||
    activeTabIndex >= Object.values(ProspectPageTabs).indexOf(ProspectPageTabs.SCENARIO);

  const sharedDetailsProps = {
    firstName: firstName.value,
    lastName: lastName.value,
    accountName: showAccountName ? accountName.value : undefined,
    jobTitle: showJobTitle ? jobTitle.value : undefined,
  };

  // Drafts can't be favorited.
  const showFavoriteButton = personaId && isFavorited !== undefined && !isDraft;

  // Handles navigating the super-admins to the Prompt tab after regenerating.
  const onSuperAdminRegenerateSuccess = useCallback(
    (newData: UpdatedPracticeProspectData) => {
      const { personaPrompt: newPersonaPrompt, isPromptEdited: newIsPromptEdited } = newData.prospect;

      // We update the form with the new data.
      dispatch(setPersonaPrompt({ value: newPersonaPrompt, isUserModified: newIsPromptEdited }));
      dispatch(
        setProspectForm({
          prospect: {
            ...newData.prospect,
            isSubscribed,
            allowedCallers,
            allowedTeams,
          },
          canManageProspect: true,
        })
      );

      setActiveTab(ProspectPageTabs.PROMPT);
      setRegeneratePromptDialog({ open: false, isDraft: false });
    },
    [dispatch, isSubscribed, allowedCallers, allowedTeams, setActiveTab]
  );

  // Handles regenerating the prompt.
  // For super-admins, if regenerating the prompt is successful, we navigate to the prompt tab.
  const handleRegeneratePrompt = useCallback(async () => {
    if (!personaId) return;
    try {
      const response = await regeneratePrompt(personaId);
      handleApiResponse({
        response,
        errorMsg: REGENERATE_PROMPT_ERROR_MSG,
        hideErrorToast: !isSuperAdmin,
        hideSuccessToast: !isSuperAdmin, // Hide toasts for non-super-admins because it is auto-generated and they do not have access to the prompt.
        onSuccess: isSuperAdmin ? onSuperAdminRegenerateSuccess : undefined, // For non-super-admins, regenerating the prompt happens in the background.
      });
    } catch (error) {
      console.error(error);
      if (isSuperAdmin) {
        showToast({ message: REGENERATE_PROMPT_ERROR_MSG, type: AlertType.ERROR });
      }
    }
  }, [personaId, handleApiResponse, isSuperAdmin, onSuperAdminRegenerateSuccess, regeneratePrompt, showToast]);

  // Since the prompt was regenerated, we navigate to the Simulations page with the correct tab.
  // If the prospect is a draft, we navigate to the drafts tab.
  // Otherwise, we navigate to the library tab.
  const handleSaveWithoutRegenerating = useCallback(() => {
    const navigateToTab = regeneratePromptDialog.isDraft ? SimulationsTab.DRAFTS : SimulationsTab.LIBRARY;
    const searchParams = new URLSearchParams();
    searchParams.set(ACTIVE_TAB_PARAM, navigateToTab);
    navigate({ pathname: AppRoutes.SIMULATIONS, search: searchParams.toString() });
  }, [regeneratePromptDialog, navigate]);

  const onFavoriteSuccess = useCallback(
    (newIsFavorited: boolean) => {
      dispatch(updateFooterFields({ isFavorited: newIsFavorited }));
    },
    [dispatch]
  );

  return (
    <>
      <div className="relative z-50 w-full border-t border-base-300 bg-base-150 p-8 shadow-[0px_0px_5px_0px_rgba(91,94,93,0.20)_inset]">
        <div className="flex w-full items-center justify-end">
          <div className="flex h-full w-full items-center gap-8">
            {showProspectDetails && (
              <div className="min-w-[20%]">
                {showFavoriteButton && (
                  <ProspectDetails
                    {...sharedDetailsProps}
                    personaId={personaId}
                    isFavorited={isFavorited}
                    onFavoriteSuccess={onFavoriteSuccess}
                  />
                )}
                {!showFavoriteButton && <ProspectDetails {...sharedDetailsProps} />}
              </div>
            )}
            {showTagsAndNotes && (
              <>
                <Divider type={Orientation.VERTICAL} />
                <TagsAndNotesCell
                  prospectId={personaId}
                  prospectTags={tags}
                  notes={notes}
                  onApplyNotes={(notes) => dispatch(updateFooterFields({ notes }))}
                  onApplyTags={(selectedTags) => dispatch(updateFooterFields({ tags: selectedTags }))}
                />
              </>
            )}
            <div className="ml-auto flex items-center justify-end gap-8" style={{ height: PROSPECT_FOOTER_HEIGHT }}>
              {showTagsAndNotes && <Divider type={Orientation.VERTICAL} />}
              {mode === ProspectPageMode.CREATE && (
                <CreateEndActions
                  activeTab={activeTab}
                  isSuperAdmin={isSuperAdmin}
                  visitedTabs={visitedTabs}
                  setActiveTab={setActiveTab}
                  setVisitedTabs={setVisitedTabs}
                />
              )}
              {mode === ProspectPageMode.EDIT && (
                <EditEndActions
                  isSuperAdmin={isSuperAdmin}
                  resetForm={resetForm}
                  setRegeneratePromptDialog={setRegeneratePromptDialog}
                  runRegeneratePrompt={handleRegeneratePrompt}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <Dialog isOpen={regeneratePromptDialog.open} onClose={handleSaveWithoutRegenerating} title="Regenerate prompt">
        <div className="flex flex-col gap-4">
          <Typography type={TypographyType.BASE}>
            Changes are saved. Do you want the prompt to be regenerated?
          </Typography>
          <ButtonGroup loading={isRegeneratingPrompt}>
            <TextButton text="Regenerate prompt" onClick={handleRegeneratePrompt} />
            <TextButton text="Save without regenerating" onClick={handleSaveWithoutRegenerating} />
          </ButtonGroup>
        </div>
      </Dialog>
    </>
  );
};

export default ProspectPageFooter;
